import { useState, useEffect, useCallback, useRef } from "react";
/**
 * A countdown hook that uses timestamps to handle browser tab inactivity correctly.
 * Unlike setInterval-based approaches, this calculates remaining time from the
 * target end time, so it stays accurate even when the tab is inactive.
 */
const useCountDown = ({ duration, autoStart = false, onComplete }) => {
    const [endTime, setEndTime] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(duration);
    const onCompleteRef = useRef(onComplete);
    // Keep the callback ref updated
    onCompleteRef.current = onComplete;
    const isRunning = endTime !== null && secondsLeft > 0;
    const start = useCallback(() => {
        setEndTime(Date.now() + duration * 1000);
        setSecondsLeft(duration);
    }, [duration]);
    const stop = useCallback(() => {
        setEndTime(null);
    }, []);
    const reset = useCallback(() => {
        setEndTime(null);
        setSecondsLeft(duration);
    }, [duration]);
    // Auto-start if enabled
    useEffect(() => {
        if (autoStart) {
            start();
        }
    }, [autoStart, start]);
    // Main countdown effect using timestamps
    useEffect(() => {
        if (endTime === null)
            return undefined;
        const updateCountdown = () => {
            const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
            setSecondsLeft(remaining);
            if (remaining === 0) {
                setEndTime(null);
                onCompleteRef.current?.();
            }
        };
        // Update immediately
        updateCountdown();
        // Update every second
        const intervalId = setInterval(updateCountdown, 1000);
        // Also update when tab becomes visible again
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                updateCountdown();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [endTime]);
    return {
        secondsLeft,
        isRunning,
        start,
        stop,
        reset,
    };
};
export default useCountDown;
